import { FC, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import ConfirmationModal from 'src/components/molecules/ConfirmationModal/ConfirmationModal';
import { useProofDocumentTypes } from 'src/features/proofs/hooks';
import {
  reasons as insufficientReasonTypes,
  olvInsufficientReasons,
  reasonsEditedExtended as reasonsEditedType
} from 'src/pages/ReviewPage/components/BulkSelectDoc/constants';
import { EntryResultEnum } from 'src/ts/enums';
import { IProof } from 'src/ts/interfaces';
import {
  FormValues,
  involvesOfferLetter,
  OFFER_LETTER_TYPE,
  RECLASSIFY_CHECK_SET_CONFIRM_TEXT,
  RECLASSIFY_FROM_OL_CONFIRM_TEXT,
  RECLASSIFY_TO_OL_CONFIRM_TEXT,
  RECLASSIFY_TO_OL_GATED_CONFIRM_TEXT
} from '../helpers/proofHelpers';
import { fetchReclassificationRequeue } from '../helpers/reclassificationImpact';
import {
  BottomBar,
  BottomBarContent,
  BottomBarControls,
  CustomButton,
  DocIdLabel,
  NavigationButtonsContainer,
  RailControlRow,
  RailControls,
  RailResultPlaceholder,
  RailNavigation
} from '../styles';
import CustomDropdown from './CustomDropdown';
import CustomMultiSelectDropdown from './CustomMultiSelectDropdown';
import CustomResultDropdown from './CustomResultDropdown';

interface DocumentControlsProps {
  currentProof: IProof | undefined;
  selectedProofId: string;
  watchedResult: string;
  watchedReason: string | string[];
  watchedDocumentType: string;
  watchedOlvReasons: string[];
  setValue: UseFormSetValue<FormValues>;
  isFirstDocument: boolean;
  isLastDocument: boolean;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
  onNextDocument: () => void;
  onPreviousDocument: () => void;
  onReviewReport: () => void;
  proofs: IProof[];
  localFormData: Record<string, FormValues>;
  hideResultControls?: boolean;
  isOlvActionCommitted?: boolean;
  onReclassifyToOfferLetter?: (proofId: string) => void;
  // ITL-2772: FROM-OL changes must also commit synchronously to the BE
  // (see ModernizedFraudAnalystReview handler comment). Symmetric with
  // onReclassifyToOfferLetter — the only difference is no carousel
  // advance / nav after success.
  onReclassifyFromOfferLetter?: (proofId: string, newType: IProof['type']) => void;
  // Resolves true when the analyst stays on the review and the type change should apply.
  onReclassifyCheckSetChange?: (proofId: string, newType: IProof['type']) => Promise<boolean>;
  // ITL-2772: drives the pre-flight modal copy. When the property's
  // `offer_letter_verification` feature is disabled, the Greystar copy
  // (queue + AI pipeline + lost work) is actively misleading — the
  // gated copy explains the auto-Insufficient outcome instead.
  isPropertyOlvEnabled?: boolean;
  layout?: 'bar' | 'rail';
}

const DocumentControls: FC<DocumentControlsProps> = ({
  currentProof,
  selectedProofId,
  watchedResult,
  watchedReason,
  watchedDocumentType,
  watchedOlvReasons,
  setValue,
  isFirstDocument,
  isLastDocument,
  canNavigateBack,
  canNavigateForward,
  onNextDocument,
  onPreviousDocument,
  onReviewReport,
  proofs,
  localFormData,
  hideResultControls,
  isOlvActionCommitted,
  onReclassifyToOfferLetter,
  onReclassifyFromOfferLetter,
  onReclassifyCheckSetChange,
  isPropertyOlvEnabled,
  layout = 'bar'
}) => {
  const { proofDocumentTypes } = useProofDocumentTypes();
  const showOlvDropdown =
    watchedDocumentType === OFFER_LETTER_TYPE && watchedResult === EntryResultEnum.Undetermined;

  const [showReclassifyConfirm, setShowReclassifyConfirm] = useState(false);
  const [pendingTypeChange, setPendingTypeChange] = useState<string | null>(null);
  const [pendingRequeue, setPendingRequeue] = useState(false);
  const [checkingProofId, setCheckingProofId] = useState<string | null>(null);
  const checkingImpact = checkingProofId === selectedProofId;
  const selectedProofIdRef = useRef(selectedProofId);
  selectedProofIdRef.current = selectedProofId;

  const applyTypeChange = (value: string) => {
    setValue('documentType', value);
    if (
      value === OFFER_LETTER_TYPE &&
      (watchedResult === EntryResultEnum.Clean || watchedResult === EntryResultEnum.Edited)
    ) {
      setValue('result', '');
      setValue('reason', '');
    }
    // Clear OLV reasons when switching away from OFFER_LETTER
    if (value !== OFFER_LETTER_TYPE) {
      setValue('olvReasons', []);
    }
    // Clear regular reason when switching to OFFER_LETTER with UNDETERMINED
    if (value === OFFER_LETTER_TYPE && watchedResult === EntryResultEnum.Undetermined) {
      setValue('reason', '');
    }
  };

  const handleDocumentTypeChange = async (value: string) => {
    if (value === watchedDocumentType) {
      applyTypeChange(value);
      return;
    }

    if (value === OFFER_LETTER_TYPE) {
      setPendingTypeChange(value);
      setShowReclassifyConfirm(true);
      return;
    }

    const proofId = selectedProofId;
    setCheckingProofId(proofId);
    // A failed read warns anyway, so the change is never deferred to Send Report unannounced.
    const requeue = await fetchReclassificationRequeue(proofId, value).catch(() => true);
    setCheckingProofId((current) => (current === proofId ? null : current));
    // The analyst can still switch documents from the header or the document list.
    if (selectedProofIdRef.current !== proofId) return;

    if (!requeue && !involvesOfferLetter(watchedDocumentType, value)) {
      applyTypeChange(value);
      return;
    }

    setPendingRequeue(requeue);
    setPendingTypeChange(value);
    setShowReclassifyConfirm(true);
  };

  const handleConfirmReclassify = async () => {
    setShowReclassifyConfirm(false);
    setPendingRequeue(false);
    if (pendingTypeChange) {
      if (pendingTypeChange === OFFER_LETTER_TYPE && onReclassifyToOfferLetter) {
        onReclassifyToOfferLetter(selectedProofId);
      } else if (
        !involvesOfferLetter(watchedDocumentType, pendingTypeChange) &&
        onReclassifyCheckSetChange
      ) {
        const newType = pendingTypeChange;
        setPendingTypeChange(null);
        if (await onReclassifyCheckSetChange(selectedProofId, newType as IProof['type'])) {
          applyTypeChange(newType);
        }
        return;
      } else {
        applyTypeChange(pendingTypeChange);
        // ITL-2772: commit FROM-OL synchronously to the BE so the DB
        // reflects the new type. Without this, a later switch back to
        // OFFER_LETTER produces a same-type PATCH which bypasses
        // reclassification (see handler comment in parent).
        if (onReclassifyFromOfferLetter) {
          onReclassifyFromOfferLetter(selectedProofId, pendingTypeChange as IProof['type']);
        }
      }
    }
    setPendingTypeChange(null);
  };

  const setShowReclassifyConfirmWithCleanup = (show: boolean) => {
    setShowReclassifyConfirm(show);
    if (!show) {
      setPendingTypeChange(null);
      setPendingRequeue(false);
    }
  };

  const reclassifyModalText =
    pendingTypeChange === OFFER_LETTER_TYPE
      ? isPropertyOlvEnabled
        ? RECLASSIFY_TO_OL_CONFIRM_TEXT
        : RECLASSIFY_TO_OL_GATED_CONFIRM_TEXT
      : pendingRequeue
      ? RECLASSIFY_CHECK_SET_CONFIRM_TEXT
      : RECLASSIFY_FROM_OL_CONFIRM_TEXT;

  const getReasonOptions = () => {
    if (watchedResult === EntryResultEnum.Edited) {
      return reasonsEditedType.map((reason) => ({ value: reason.value, label: reason.label }));
    } else if (watchedResult === EntryResultEnum.Undetermined) {
      return insufficientReasonTypes.map((reason) => ({
        value: reason.value,
        label: reason.label
      }));
    }
    return [];
  };

  const showReasonDropdown =
    watchedResult === EntryResultEnum.Edited || watchedResult === EntryResultEnum.Undetermined;

  const isEditedResult = watchedResult === EntryResultEnum.Edited;

  const documentTypeOptions = proofDocumentTypes.map((docType) => ({
    value: docType.value,
    label: docType.label
  }));

  const docIdLabel = (
    <DocIdLabel id="fraud-doc-id-label" data-testid="fraud-doc-id-label">
      Doc ID: {currentProof?.short_id || selectedProofId}
    </DocIdLabel>
  );

  const resultDropdown = !hideResultControls && (
    <CustomResultDropdown
      id="fraud-result-dropdown"
      value={watchedResult}
      onChange={(value) => setValue('result', value)}
      documentType={watchedDocumentType}
    />
  );

  const reasonDropdown =
    !hideResultControls &&
    (showOlvDropdown ? (
      <CustomMultiSelectDropdown
        id="fraud-olv-reason-selector"
        value={watchedOlvReasons}
        onChange={(values) => setValue('olvReasons', values)}
        options={olvInsufficientReasons}
        placeholder="Select Reasons"
      />
    ) : showReasonDropdown ? (
      isEditedResult ? (
        <CustomMultiSelectDropdown
          id="fraud-edited-reason-selector"
          value={
            Array.isArray(watchedReason) ? watchedReason : watchedReason ? [watchedReason] : []
          }
          onChange={(values) => setValue('reason', values)}
          options={getReasonOptions()}
          placeholder="Select Reasons"
        />
      ) : (
        <CustomDropdown
          id="fraud-insufficient-reason-selector"
          value={Array.isArray(watchedReason) ? watchedReason[0] || '' : watchedReason || ''}
          onChange={(value) => setValue('reason', value)}
          options={getReasonOptions()}
          placeholder="Select Reason"
        />
      )
    ) : null);

  const documentTypeDropdown = (
    <CustomDropdown
      id="fraud-document-type-dropdown"
      value={watchedDocumentType}
      onChange={handleDocumentTypeChange}
      options={documentTypeOptions}
      placeholder="Document Type"
      disabled={isOlvActionCommitted || checkingImpact}
    />
  );

  const backButton = (
    <CustomButton
      data-testid="review-previous-document"
      onClick={onPreviousDocument}
      disabled={checkingImpact || !canNavigateBack}
    >
      <img src="/assets/images/arrow-left.svg" alt="Back" /> Back
    </CustomButton>
  );

  const reviewSubmissionButton = (
    <CustomButton
      id="review-submission-button"
      data-testid="review-submission-button"
      onClick={onReviewReport}
      disabled={checkingImpact}
    >
      Review submission
    </CustomButton>
  );

  const forwardButton = canNavigateForward ? (
    <CustomButton
      data-testid="review-next-document"
      onClick={onNextDocument}
      disabled={checkingImpact}
    >
      Next <img src="/assets/images/arrow-right.svg" alt="Next" />
    </CustomButton>
  ) : (
    reviewSubmissionButton
  );

  const reclassifyModal = (
    <ConfirmationModal
      showConfirmationModal={showReclassifyConfirm}
      setShowConfirmationModal={setShowReclassifyConfirmWithCleanup}
      confirmationAction={handleConfirmReclassify}
      modalTitle="Reclassify Document Type"
      modalText={reclassifyModalText}
      confirmButtonLabel="Continue"
      cancelButtonLabel="Cancel"
    />
  );

  if (layout === 'rail') {
    return (
      <RailControls data-testid="fraud-rail-controls">
        {docIdLabel}
        <RailControlRow>{documentTypeDropdown}</RailControlRow>
        {resultDropdown ? (
          <RailControlRow data-testid="fraud-rail-result-row">
            {resultDropdown}
            {reasonDropdown}
          </RailControlRow>
        ) : (
          <RailControlRow>
            <RailResultPlaceholder
              isOpen={false}
              aria-hidden="true"
              data-testid="fraud-rail-result-placeholder"
            >
              &nbsp;
            </RailResultPlaceholder>
          </RailControlRow>
        )}
        <RailNavigation>
          {currentProof?.is_automatic_review ? (
            reviewSubmissionButton
          ) : (
            <>
              {backButton}
              {forwardButton}
            </>
          )}
        </RailNavigation>
        {reclassifyModal}
      </RailControls>
    );
  }

  return (
    <BottomBar>
      <BottomBarContent>
        {docIdLabel}

        <BottomBarControls>
          {resultDropdown}
          {reasonDropdown}
          {documentTypeDropdown}
          <NavigationButtonsContainer>
            {canNavigateBack && backButton}
            {forwardButton}
          </NavigationButtonsContainer>
        </BottomBarControls>
      </BottomBarContent>

      {reclassifyModal}
    </BottomBar>
  );
};

export default DocumentControls;
